import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Zap, Check, ArrowRight, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL;

const PACKS = [
  {
    id: 'starter',
    name: 'Starter',
    credits: 50,
    price: '4,90€',
    amount: 4.90,
    desc: 'Pour tester et démarrer',
    color: '#94a3b8',
    perCredit: '0,10€/crédit',
  },
  {
    id: 'confort',
    name: 'Confort',
    credits: 150,
    price: '12,90€',
    amount: 12.90,
    desc: 'Usage régulier',
    color: '#20c997',
    perCredit: '0,086€/crédit',
  },
  {
    id: 'pro',
    name: 'Pro',
    credits: 350,
    price: '27,90€',
    amount: 27.90,
    desc: 'Usage intensif',
    color: '#ffd93d',
    popular: true,
    perCredit: '0,080€/crédit',
  },
  {
    id: 'intensif',
    name: 'Intensif',
    credits: 800,
    price: '54,90€',
    amount: 54.90,
    desc: 'Production massive',
    color: '#ff6b6b',
    perCredit: '0,069€/crédit',
  },
  {
    id: 'equipe',
    name: 'Équipe',
    credits: 2000,
    price: '129€',
    amount: 129,
    desc: 'Pour les équipes RH',
    color: '#a78bfa',
    perCredit: '0,065€/crédit',
  },
];

const ACTION_COSTS = [
  { key: 'credits.costCourse', cost: 10, color: '#ff6b6b' },
  { key: 'credits.costChat', cost: 1, color: '#20c997' },
  { key: 'credits.costGap', cost: 3, color: '#ffd93d' },
  { key: 'credits.costQuiz', cost: 2, color: '#ffd93d' },
  { key: 'credits.costPdf', cost: 2, color: '#94a3b8' },
  { key: 'credits.costScorm', cost: 3, color: '#94a3b8' },
];

export default function Credits() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(false);

  useEffect(() => {
    loadBalance();
    loadTransactions();
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) pollPackStatus(sessionId);
  }, []);

  const loadBalance = async () => {
    try {
      const res = await axios.get(`${API}/api/credits/balance`, { withCredentials: true });
      setBalance(res.data);
    } catch (_) {}
  };

  const loadTransactions = async () => {
    setTxLoading(true);
    try {
      const res = await axios.get(`${API}/api/credits/transactions`, { withCredentials: true });
      setTransactions(res.data || []);
    } catch (_) {} finally {
      setTxLoading(false);
    }
  };

  const pollPackStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 8) return;
    try {
      const res = await axios.get(`${API}/api/credits/pack-status/${sessionId}`, { withCredentials: true });
      if (res.data.payment_status === 'paid') {
        toast.success('Crédits ajoutés avec succès !');
        await loadBalance();
        await loadTransactions();
        return;
      }
      setTimeout(() => pollPackStatus(sessionId, attempts + 1), 2000);
    } catch (_) {}
  };

  const handleBuyPack = async (packId) => {
    if (!user) { navigate('/auth?mode=register'); return; }
    setLoading(packId);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/api/credits/checkout`, { pack_id: packId, origin_url: origin }, { withCredentials: true });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du checkout');
      setLoading(null);
    }
  };

  const pct = balance ? (balance.monthly_credits > 0
    ? Math.min((balance.balance / balance.monthly_credits) * 100, 100)
    : (balance.balance > 0 ? 100 : 0)) : 0;
  const gaugeColor = pct > 50 ? '#20c997' : pct > 20 ? '#ffd93d' : '#ff6b6b';

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-10">

        {/* Header + balance */}
        <div>
          <h1 className="font-heading text-3xl font-black text-white mb-1">{t('credits.title')}</h1>
          <p className="text-[#94a3b8] text-sm">{t('credits.subtitle')}</p>
        </div>

        {/* Balance card */}
        {balance && (
          <div className="bg-[#16161e] border border-[#2a2935] rounded-2xl p-6 flex flex-col sm:flex-row gap-6 items-start sm:items-center" data-testid="credits-balance-card">
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#64748b] mb-1">{t('credits.currentBalance')}</p>
              <div className="flex items-baseline gap-2 mb-3">
                <span className="font-heading text-5xl font-black" style={{ color: gaugeColor }}>{balance.balance}</span>
                <span className="text-[#94a3b8] text-sm">{t('credits.credits')}</span>
              </div>
              {balance.monthly_credits > 0 && (
                <>
                  <div className="h-2 w-full bg-[#2a2935] rounded-full overflow-hidden mb-1">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: `linear-gradient(90deg, #ffd93d, ${gaugeColor})` }} />
                  </div>
                  <p className="text-xs text-[#64748b]">{t('credits.monthlyLine', { balance: balance.balance, monthly: balance.monthly_credits, plan: balance.plan })}</p>
                </>
              )}
              {balance.plan === 'explorateur' && (
                <p className="text-xs text-[#20c997] mt-2" data-testid="free-plan-credits-line">{t('credits.freePlanLine')}</p>
              )}
            </div>
            <div className="flex flex-col gap-2 text-sm text-[#94a3b8]">
              <div className="flex items-center gap-2">
                <Zap size={14} className="text-[#ffd93d]" />
                <span>{t('credits.usedTotal', { n: balance.lifetime_used })}</span>
              </div>
              {balance.purchased_credits > 0 && (
                <div className="flex items-center gap-2">
                  <Check size={14} className="text-[#20c997]" />
                  <span>{t('credits.purchased', { n: balance.purchased_credits })}</span>
                </div>
              )}
              <button onClick={loadBalance} className="flex items-center gap-1.5 text-xs text-[#64748b] hover:text-white transition-colors mt-1">
                <RefreshCw size={12} />{t('credits.refresh')}
              </button>
            </div>
          </div>
        )}

        {/* Packs */}
        <div>
          <h2 className="font-heading text-xl font-bold text-white mb-1">{t('credits.packsTitle')}</h2>
          <p className="text-[#94a3b8] text-sm mb-5">{t('credits.packsSubtitle')}</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="packs-grid">
            {PACKS.map((pack) => (
              <div
                key={pack.id}
                className={`relative bg-[#16161e] border rounded-2xl p-5 flex flex-col gap-4 transition-all duration-200 ${pack.popular ? 'border-[#ffd93d]/40' : 'border-[#2a2935] hover:border-[#3a3945]'}`}
                data-testid={`pack-${pack.id}`}
              >
                {pack.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#ffd93d] text-[#0f0e17] text-[10px] font-bold px-3 py-0.5 rounded-full uppercase tracking-wide">
                    {t('credits.bestValue')}
                  </span>
                )}

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${pack.color}20` }}>
                      <Zap size={15} style={{ color: pack.color }} />
                    </div>
                    <span className="font-heading font-bold text-white">{pack.name}</span>
                  </div>
                  <span className="font-heading text-xl font-black" style={{ color: pack.color }}>{pack.price}</span>
                </div>

                <div>
                  <p className="text-2xl font-black text-white">{pack.credits} <span className="text-[#94a3b8] text-sm font-normal">{t('credits.credits')}</span></p>
                  <p className="text-xs text-[#64748b] mt-0.5">{pack.perCredit}</p>
                </div>

                <p className="text-sm text-[#94a3b8]">{pack.desc}</p>

                <button
                  onClick={() => handleBuyPack(pack.id)}
                  disabled={loading === pack.id}
                  className="w-full py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all"
                  style={{ backgroundColor: pack.color, color: '#0f0e17' }}
                  data-testid={`buy-pack-${pack.id}`}
                >
                  {loading === pack.id ? (
                    <div className="w-4 h-4 border-2 border-[#0f0e17] border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>{t('credits.buy')} <ArrowRight size={14} /></>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Action costs table */}
        <div>
          <h2 className="font-heading text-xl font-bold text-white mb-4">{t('credits.costsTitle')}</h2>
          <div className="bg-[#16161e] border border-[#2a2935] rounded-2xl overflow-hidden">
            {ACTION_COSTS.map((item, i) => (
              <div key={i} className={`flex items-center justify-between px-5 py-3.5 ${i < ACTION_COSTS.length - 1 ? 'border-b border-[#2a2935]' : ''}`}>
                <span className="text-sm text-[#e8e8e8]">{t(item.key)}</span>
                <div className="flex items-center gap-1.5">
                  <Zap size={12} style={{ color: item.color }} />
                  <span className="font-bold text-sm" style={{ color: item.color }}>{item.cost} {item.cost > 1 ? t('credits.creditUnits') : t('credits.creditUnit')}</span>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#20c997] mt-3 flex items-center gap-1.5" data-testid="refund-guarantee-note"><Check size={12} /> {t('credits.refundNote')}</p>
        </div>

        {/* Transactions */}
        {transactions.length > 0 && (
          <div>
            <h2 className="font-heading text-xl font-bold text-white mb-4">{t('credits.historyTitle')}</h2>
            <div className="bg-[#16161e] border border-[#2a2935] rounded-2xl overflow-hidden" data-testid="transactions-list">
              {transactions.slice(0, 20).map((tx, i) => (
                <div key={tx.created_at + i} className={`flex items-center justify-between px-5 py-3.5 ${i < transactions.length - 1 ? 'border-b border-[#2a2935]' : ''}`} data-testid={`transaction-${tx.action}`}>
                  <div>
                    <p className="text-sm text-white">{t(`credits.actions.${tx.action}`, { defaultValue: (tx.action || '').replace(/_/g, ' ') })}</p>
                    <p className="text-xs text-[#64748b]">{new Date(tx.created_at).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'fr-FR')}</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Zap size={12} className={tx.amount > 0 ? 'text-[#20c997]' : 'text-[#ff6b6b]'} />
                    <span className={`font-bold text-sm ${tx.amount > 0 ? 'text-[#20c997]' : 'text-[#ff6b6b]'}`}>
                      {tx.amount > 0 ? '+' : ''}{tx.amount}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
